import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import { EServiceTemplateDataProcessingInCountriesSpecialTreatment, ServiceTemplateField, isServiceAllFieldsByCdnState } from "@devowl-wp/api-real-cookie-banner";
import { EIso3166OneAlpha2 } from "@devowl-wp/iso-codes";
import { Trans } from "@devowl-wp/react-frontend";
import { useServiceCdnState } from "../../../../../../../hooks/service/useServiceCdnState.js";
import { useTranslation } from "../../../../../../../others/i18n.js";
/**
 * Custom hook to handle special treatments for data processing in countries.
 * If the CDN is enabled, the special treatment for standard contractual clauses is still available.
 *
 * @returns An object containing special treatments and a function to calculate unsafe countries.
 */ function useServiceDataProcessingCountriesSpecialTreatments() {
    const { t } = useTranslation();
    const cdnFieldState = useServiceCdnState(ServiceTemplateField.DataProcessingInCountriesSpecialTreatments);
    const isFormFullyVisible = useMemo(()=>isServiceAllFieldsByCdnState(cdnFieldState), [
        cdnFieldState
    ]);
    return useMemo(()=>{
        const specialTreatments = [
            {
                label: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        t("Standard contractual clauses concluded with the provider"),
                        /*#__PURE__*/ _jsx("div", {
                            className: `${process.env.ANTD_PREFIX}-form-item-extra`,
                            style: {
                                margin: "0px 0px 10px 0px"
                            },
                            children: /*#__PURE__*/ _jsxs(Trans, {
                                children: [
                                    "Did you sign a contract with the provider, e.g. when registering online? If so, the contract may contain",
                                    " ",
                                    /*#__PURE__*/ _jsx("a", {
                                        href: t("https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection/standard-contractual-clauses-scc_en"),
                                        rel: "noreferrer",
                                        target: "_blank",
                                        children: "standard contractual clauses"
                                    }),
                                    " ",
                                    "of the EU, which promise secure data processing in unsafe third countries."
                                ]
                            })
                        })
                    ]
                }),
                value: EServiceTemplateDataProcessingInCountriesSpecialTreatment.StandardContractualClauses
            }
        ];
        if (isFormFullyVisible) {
            specialTreatments.push(...[
                {
                    label: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            t("Standard contractual clauses concluded by the provider with sub-processors processing data in unsafe third countries, contractually assured"),
                            /*#__PURE__*/ _jsx("div", {
                                className: `${process.env.ANTD_PREFIX}-form-item-extra`,
                                style: {
                                    margin: "0px 0px 10px 0px"
                                },
                                children: /*#__PURE__*/ _jsxs(Trans, {
                                    children: [
                                        "Did you sign a contract with the provider from a safe country, e.g. when registering online? If so, the contract may contain information (e.g. in a data processing agreement) if the service provider has concluded",
                                        " ",
                                        /*#__PURE__*/ _jsx("a", {
                                            href: t("https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection/standard-contractual-clauses-scc_en"),
                                            rel: "noreferrer",
                                            target: "_blank",
                                            children: "standard contractual clauses"
                                        }),
                                        " ",
                                        "of the EU ",
                                        /*#__PURE__*/ _jsx("u", {
                                            children: "with his own processors"
                                        }),
                                        ", which promise secure data processing in unsafe third countries."
                                    ]
                                })
                            })
                        ]
                    }),
                    value: EServiceTemplateDataProcessingInCountriesSpecialTreatment.ContractualAssurancesWithSubProcessors
                },
                {
                    label: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            t("Trans-Atlantic Data Privacy Framework self-certified provider for secure data processing in the USA"),
                            /*#__PURE__*/ _jsx("div", {
                                className: `${process.env.ANTD_PREFIX}-form-item-extra`,
                                style: {
                                    margin: "0px 0px 10px 0px"
                                },
                                children: /*#__PURE__*/ _jsxs(Trans, {
                                    children: [
                                        'The adequacy decision for secure data processing only applies if the provider is self certified and included in the "Data Privacy Framework List". To find out which companies are certified,',
                                        " ",
                                        /*#__PURE__*/ _jsx("a", {
                                            href: t("https://www.dataprivacyframework.gov/list"),
                                            rel: "noreferrer",
                                            target: "_blank",
                                            children: "visit the website of the US government"
                                        }),
                                        "."
                                    ]
                                })
                            })
                        ]
                    }),
                    value: EServiceTemplateDataProcessingInCountriesSpecialTreatment.ProviderIsSelfCertifiedTransAtlanticDataPrivacyFramework
                }
            ]);
        }
        return {
            specialTreatments,
            bySelectedCountries: (dataProcessingInCountries)=>{
                return specialTreatments.filter(({ value })=>{
                    switch(value){
                        case EServiceTemplateDataProcessingInCountriesSpecialTreatment.ProviderIsSelfCertifiedTransAtlanticDataPrivacyFramework:
                            return dataProcessingInCountries.indexOf(EIso3166OneAlpha2.UnitedStates) > -1;
                        default:
                            return true;
                    }
                });
            }
        };
    }, [
        isFormFullyVisible
    ]);
}
export { useServiceDataProcessingCountriesSpecialTreatments };
